import React, { useState, useEffect, useRef } from "react";
import { CustomButton } from "../atoms/CustomButton";
import { ModalShell } from "../atoms/ModalShell";
import { useTranslation } from "react-i18next";
import NoTranslate from "../atoms/NoTranslate";

interface PhoneValidationModalProps {
  show: boolean;
  onClose: () => void;
  onConfirm: (code: string) => void;
  onResend: () => void;
  expiresAt: number;
  loading: boolean;
  phoneNumber: string;
  namespace?: "translation" | "clientView";
  /** The server refused the code for good: show it expired, with the new code link, before the timer ends. */
  codeSpent?: boolean;
}

const makeEmptyDigits = (): string[] => ["", "", "", "", "", ""];

export const PhoneValidationModal: React.FC<PhoneValidationModalProps> = ({
  show,
  onClose,
  onConfirm,
  onResend,
  expiresAt,
  loading,
  phoneNumber,
  namespace = "translation",
  codeSpent = false,
}) => {
  const { t } = useTranslation(namespace);
  const [digits, setDigits] = useState<string[]>(makeEmptyDigits);
  const [timer, setTimer] = useState(0);
  const [isReady, setIsReady] = useState(false);

  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);
  const autoSubmitTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    if (show) setDigits(makeEmptyDigits());
  }, [show]);

  useEffect(() => {
    if (!show || !expiresAt || expiresAt <= Date.now()) {
      setIsReady(false);
      return;
    }

    setIsReady(true);

    const tick = () => {
      // Rounded up: the timer reaches 0 only once the code has expired, which renders the new code link.
      const remaining = Math.max(0, Math.ceil((expiresAt - Date.now()) / 1000));
      setTimer(remaining);
      if (remaining === 0) window.clearInterval(interval);
    };
    const interval = window.setInterval(tick, 1000);
    tick();

    return () => window.clearInterval(interval);
  }, [show, expiresAt]);

  useEffect(() => {
    if (!show && autoSubmitTimeoutRef.current !== null) {
      window.clearTimeout(autoSubmitTimeoutRef.current);
      autoSubmitTimeoutRef.current = null;
    }
    return () => {
      if (autoSubmitTimeoutRef.current !== null) {
        window.clearTimeout(autoSubmitTimeoutRef.current);
        autoSubmitTimeoutRef.current = null;
      }
    };
  }, [show]);

  const isExpired = codeSpent || (expiresAt > 0 && expiresAt <= Date.now());

  const handleChange = (index: number, value: string) => {
    if (!/^\d?$/.test(value)) return;

    const newDigits = [...digits];
    newDigits[index] = value;
    setDigits(newDigits);

    if (value && index < 5) {
      inputsRef.current[index + 1]?.focus();
    }

    if (value && index === 5 && newDigits.every((d) => d !== "")) {
      autoSubmitTimeoutRef.current = window.setTimeout(() => {
        autoSubmitTimeoutRef.current = null;
        onConfirm(newDigits.join(""));
      }, 100);
    }
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === "Backspace") {
      if (digits[index]) {
        const newDigits = [...digits];
        newDigits[index] = "";
        setDigits(newDigits);
      } else if (index > 0) {
        inputsRef.current[index - 1]?.focus();

        const newDigits = [...digits];
        newDigits[index - 1] = "";
        setDigits(newDigits);
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();

    const pasted = e.clipboardData.getData("text").trim().slice(0, 6);
    if (!/^\d+$/.test(pasted)) return;

    const newDigits = pasted.split("").concat(Array(6).fill("")).slice(0, 6);
    setDigits(newDigits);

    const lastIndex = newDigits.findIndex((d) => d === "");
    inputsRef.current[lastIndex === -1 ? 5 : lastIndex]?.focus();

    if (pasted.length === 6) {
      autoSubmitTimeoutRef.current = window.setTimeout(() => {
        autoSubmitTimeoutRef.current = null;
        onConfirm(pasted);
      }, 100);
    }
  };

  const handleConfirm = () => {
    onConfirm(digits.join(""));
  };

  const formatTimer = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  return (
    <ModalShell
      show={show && isReady}
      onClose={onClose}
      size="md"
      title={t("phoneValidation.title")}
      footer={
        <CustomButton
          variant="primary"
          onClick={handleConfirm}
          disabled={digits.some((d) => !d) || loading || isExpired}
          isLoading={loading}
          title={
            loading
              ? t("phoneValidation.verifying")
              : t("phoneValidation.verifyCode")
          }
        />
      }
    >
      <div className="flex flex-col items-center gap-6">
        <div className="text-center">
          <p className="text-sm text-gray-500">{t("phoneValidation.sentTo")}</p>
          <p className="text-lg font-bold text-gray-900 mt-1">{phoneNumber}</p>
          {timer > 0 && !isExpired && (
            <div className="text-xs mt-2">
              <span className="text-gray-500">
                {t("phoneValidation.expiresIn")}{" "}
                <span className="font-semibold text-blue-600">
                  {formatTimer(timer)}
                </span>
              </span>
            </div>
          )}
        </div>

        <NoTranslate>
          <div className="flex gap-1">
            {digits.map((digit, index) => (
              <input
                key={index}
                ref={(el) => {
                  inputsRef.current[index] = el;
                }}
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                aria-label={`${t("phoneValidation.codeLabel")} ${index + 1}`}
                value={digit}
                maxLength={1}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={handlePaste}
                disabled={loading || isExpired}
                className={`
                    w-10 h-14
                    text-center
                    text-lg
                    p-2
                    font-semibold
                    border-2
                    rounded-lg
                    transition-all
                    outline-none
                    ${
                      isExpired
                        ? "border-red-300 bg-red-50"
                        : digit
                        ? "border-blue-500 bg-blue-50"
                        : "border-gray-400 bg-white"
                    }
                    focus:border-blue-600
                    focus:shadow-sm
                    disabled:opacity-50
                    disabled:cursor-not-allowed
                  `}
              />
            ))}
          </div>
        </NoTranslate>

        {isExpired && (
          <div className="text-center">
            <p className="text-sm text-red-600 font-medium mb-3">
              {codeSpent
                ? t("phoneValidation.codeSpent")
                : t("phoneValidation.expired")}
            </p>
            <button
              onClick={() => {
                setDigits(makeEmptyDigits());
                onResend();
              }}
              disabled={loading}
              className="text-sm font-medium text-blue-600 hover:text-blue-700 hover:underline cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t("phoneValidation.sendNewCode")}
            </button>
          </div>
        )}
      </div>
    </ModalShell>
  );
};

export default PhoneValidationModal;
